import { useForm } from 'react-hook-form'
import {
  forwardRef,
  type ForwardedRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text'
import { Icon } from '@wartek-id/icon-v2'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'

import KinerjaDropdownGroup from 'app/KinerjaPage/components/KinerjaDropdown/KinerjaDropdownGroup'
import useContentContext from 'app/KinerjaPage/hooks/useContentContext'
import StickyFooter from 'app/KinerjaPage/components/StickyFooter'
import useToggleSdkContext from 'utils/useToogleSdk'
import useSKP from 'app/KinerjaPage/hooks/useSKP'
import PanelKinerja from 'app/KinerjaPage/components/PanelKinerja'
import {
  skpStatusPerencanaanFinal,
  skpStatusWaitingApproval,
} from 'app/KinerjaPage/constants'
import { PANDUAN_PENGELOLAAN_GURU_PENGEMBANGAN_KOMPETENSI_URL } from 'app/KinerjaShared/utils/panduan'

import type {
  KinerjaPerencanaanTabBaseProps,
  KinerjaSelection,
  SKPTabHandler,
  UpdateSKPPayload,
} from 'types/kinerja'

import ImgDocument from 'assets/icons/img-document.svg'

export const MIN_POINT = 32

const rules = [
  `Anda diharapkan memiliki minimal ${MIN_POINT} poin per semester untuk memenuhi ekspektasi atasan.`,
  'Jika kurang, mohon diskusikan dengan atasan Anda. Jika lebih, ekspektasi atasan akan semakin lebih baik juga.',
  'Pilihan Rencana Hasil Kerja memiliki poin yang berbeda berdasarkan tingkat keterlibatan dan dampaknya.',
]

interface PerencanaanTabPengembanganKompetensiProps
  extends KinerjaPerencanaanTabBaseProps {
  customHeader?: () => JSX.Element
  customStickyFooter?: () => JSX.Element
  editReviewData?: boolean
  adjustmentInfoText?: string
  showCheckButton?: boolean
  onChange?: (values: KinerjaSelection[]) => void
  allowDeleteSingleValue?: boolean
  hideFooter?: boolean
  className?: string
}

const filterEmptyValues = (value: KinerjaSelection) => value.id !== 0

const TabPengembanganKompetensi = (
  {
    skp,
    isSubmitting = false,
    onClickNext = () => {},
    onClickSave = () => {},
    editReviewData = false,
    onChange = () => {},
    allowDeleteSingleValue = true,
    adjustmentInfoText,
    showCheckButton,
    customHeader,
    customStickyFooter,
    reviewMode = false,
    hideFooter = false,
    className,
  }: PerencanaanTabPengembanganKompetensiProps,
  ref: ForwardedRef<SKPTabHandler>
) => {
  const { getChangesPengembanganKompetensiByIndex } = useSKP(skp)
  const [isDialogConfirmationOpen, setIsDialogConfirmationOpen] =
    useState(false)
  const { contentPengembanganKompetensi, getPengembanganKompetensiById } =
    useContentContext()
  const toggles = useToggleSdkContext()

  const isHidePointEnabled =
    toggles?.['kinerja-hide-rhk-point-enabled'] === true
  const isTugasPokokEnabled =
    toggles?.['kinerja-tugas-pokok-enabled'] === true && skp?.tahun > 2024
  const isNewFlow = isHidePointEnabled && skp.tahun > 2024

  const isWaitingApproval =
    skpStatusWaitingApproval.includes(skp.status) && !reviewMode
  const isApproved = skp.status === 'APPROVED'

  const initValues =
    (editReviewData || reviewMode
      ? skp?.reviewPengembanganKompetensi
      : skp?.pengembanganKompetensi) ?? []
  const { setValue, getValues, handleSubmit, watch } = useForm<{
    pengembanganKompetensi: KinerjaSelection[]
  }>({
    defaultValues: {
      pengembanganKompetensi: initValues,
    },
  })

  const selectedValues = watch('pengembanganKompetensi')
  const isDisabledSubmitButton = selectedValues.length === 0

  const totalSelectedPoint = selectedValues.reduce((prev, curr) => {
    const pengembanganKompetensi = getPengembanganKompetensiById(curr.id)
    const currentTotalValue =
      curr.kuantitas * (pengembanganKompetensi?.bobot ?? 0)

    return prev + currentTotalValue
  }, 0)
  const pointsNeeded = MIN_POINT - totalSelectedPoint

  const handleChange = (values: KinerjaSelection[]) => {
    setValue('pengembanganKompetensi', values.filter(filterEmptyValues))

    onChange(values.filter(filterEmptyValues))
  }

  const generatePayload = useCallback((): UpdateSKPPayload => {
    return {
      id: skp.id,
      pengembanganKompetensi: getValues('pengembanganKompetensi').filter(
        filterEmptyValues
      ),
    }
  }, [skp, getValues])

  const onSubmit = () => {
    if (pointsNeeded > 0 && !isNewFlow) {
      setIsDialogConfirmationOpen(true)
    } else {
      submitForm()
    }
  }

  const submitForm = () => {
    onClickNext(generatePayload())
  }

  const onSaveDraft = useCallback(() => {
    onClickSave(generatePayload())
  }, [onClickSave, generatePayload])

  useImperativeHandle(
    ref,
    () => ({
      saveDraft: onSaveDraft,
    }),
    [onSaveDraft]
  )

  const isPamong = skp.tipe === 'SPNF'
  const teacherLabel = isPamong ? 'Pamong' : 'Guru'
  const originalAdjustmentLabel = `Perencanaan ${teacherLabel}`

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={clsx('p-9', className)}>
          {customHeader
            ? customHeader()
            : !reviewMode &&
              !isWaitingApproval &&
              !skpStatusPerencanaanFinal.includes(skp.status) && (
                <div className="flex gap-3 mb-9">
                  <ImgDocument className="w-16 h-16 shrink-0" />

                  {isNewFlow ? (
                    <div className="space-y-1">
                      <Text variant="display-sm">
                        Pilih 1 Indikator Kompetensi untuk menjadi fokus
                        peningkatan kinerja Anda
                      </Text>
                      <ul className="list-disc pl-5">
                        <Text as="li">
                          Diskusi bersama Kepala Sekolah terkait kegiatan yang
                          dapat dilakukan untuk meningkatkan kompetensi
                          berdasarkan indikator yang dipilih.
                        </Text>
                        <Text as="li">
                          Di tahap Pelaksanaan, Anda akan melakukan kegiatan
                          sesuai diskusi dan mengisi refleksi terhadap kegiatan
                          yang sudah Anda lakukan.
                        </Text>
                      </ul>
                    </div>
                  ) : (
                    <div>
                      <Text variant="display-sm">Pengembangan Kompetensi</Text>
                      <Text className="mt-1.5">
                        Pilih jenis Pengembangan Kompetensi yang paling efektif
                        dan berdampak untuk diri Anda.
                      </Text>
                    </div>
                  )}
                </div>
              )}

          <div className="flex flex-col gap-9">
            {!isNewFlow && (
              <div className="border border-gray-30 rounded">
                <div className="p-4 pb-5">
                  <div className="flex gap-1">
                    <Icon>error</Icon>
                    <Text variant="action">
                      Berdasarkan Perdirjen GTK Nomor 7607/B.B1/HK.03/2023:
                    </Text>
                  </div>
                  <ul className="list-disc list-inside pl-2">
                    {rules.map((rule, i) => (
                      <li key={`rule-${i}`} className="list-item">
                        <Text color="subdued" as="span">
                          {rule}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-blue-5 py-2 pl-7 pr-3 flex justify-between">
                  <div className="flex gap-8 items-center">
                    <Text
                      variant="action"
                      color="primary"
                      className="uppercase"
                      data-testid="total-point"
                    >
                      Total poin: {totalSelectedPoint}
                    </Text>

                    {pointsNeeded > 0 && (
                      <div className="bg-[#FEF4F4] border border-red-60 rounded p-1">
                        <Text
                          variant="helper"
                          color="critical"
                          data-testid="points-needed"
                        >
                          Kurang {pointsNeeded} poin
                        </Text>
                      </div>
                    )}
                  </div>

                  <a
                    href={PANDUAN_PENGELOLAAN_GURU_PENGEMBANGAN_KOMPETENSI_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button variant="ghost">
                      <Icon color="informational" className="mr-1">
                        help
                      </Icon>{' '}
                      Bagaimana poin ini dihitung?
                    </Button>
                  </a>
                </div>
              </div>
            )}

            {isWaitingApproval ? (
              initValues.map(({ id }, i) => {
                const kinerja = getPengembanganKompetensiById(id)

                return (
                  <PanelKinerja
                    key={`pengembanganKompetensi-${i}`}
                    data-testid={`pengembanganKompetensi-${i}`}
                    number={i + 1}
                    kinerja={kinerja}
                    showDetails={false}
                  />
                )
              })
            ) : skpStatusPerencanaanFinal.includes(skp.status) ? (
              initValues.map((_, i) => {
                const { originalKinerja, kinerja } =
                  getChangesPengembanganKompetensiByIndex(i)

                return (
                  <PanelKinerja
                    key={`pengembanganKompetensi-${i}`}
                    data-testid={`pengembanganKompetensi-${i}`}
                    number={i + 1}
                    label={isApproved ? '' : undefined}
                    originalKinerja={originalKinerja}
                    kinerja={kinerja}
                    showDetails={false}
                    hideAdjustmentLabel
                    showChange
                    originalAdjustmentLabel={originalAdjustmentLabel}
                    reviewAdjustmentLabel="Penyesuaian dari Kepala Sekolah"
                  />
                )
              })
            ) : (
              <KinerjaDropdownGroup
                skp={skp}
                kinerjaType="pengembanganKompetensi"
                label={
                  isNewFlow ? 'Indikator Kompetensi' : 'Rencana Hasil Kerja'
                }
                content={contentPengembanganKompetensi}
                kinerja={initValues}
                onChange={handleChange}
                allowDeleteSingleValue={allowDeleteSingleValue}
                adjustmentInfoText={adjustmentInfoText}
                allowAdd={!isNewFlow}
                showPoint={!isNewFlow}
                showDetails={!isNewFlow}
                showNote={!isNewFlow}
                hideAdjustmentLabel
                showCheckButton={showCheckButton}
                showChanges={editReviewData || reviewMode}
                {...(editReviewData
                  ? {
                      originalKinerja: initValues,
                    }
                  : {})}
              />
            )}
          </div>
        </div>

        {!hideFooter && (
          <StickyFooter>
            {customStickyFooter ? (
              customStickyFooter()
            ) : (
              <>
                {!skpStatusPerencanaanFinal.includes(skp.status) &&
                  !isWaitingApproval && (
                    <Button
                      size="lg"
                      color="white"
                      isLoading={isSubmitting}
                      disabled={isSubmitting}
                      onClick={onSaveDraft}
                    >
                      Simpan draf
                    </Button>
                  )}
                <Button
                  size="lg"
                  color="black"
                  isLoading={isSubmitting}
                  disabled={isSubmitting || isDisabledSubmitButton}
                  type="submit"
                >
                  {isTugasPokokEnabled
                    ? 'Ke Perilaku Kerja'
                    : 'Ke Tugas Tambahan'}
                </Button>
              </>
            )}
          </StickyFooter>
        )}
      </form>

      <Dialog isOpen={isDialogConfirmationOpen}>
        <DialogOverlay />
        <DialogContent
          className="p-6"
          data-testid="popup-min-point-confirmation"
        >
          <Text variant="display-sm">
            Pengembangan Kompetensi kurang {pointsNeeded} poin. Tetap lanjut?
          </Text>
          <Text variant="body-lg" color="subdued" className="mt-1">
            Tambah poin sekarang agar sesuai dengan Ekspektasi Pimpinan. Jika
            lanjut, atasan akan menyesuaikan nanti.
          </Text>

          <div className="flex gap-4 mt-4">
            <Button
              size="lg"
              color="white"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              onClick={() => submitForm()}
            >
              Lanjut
            </Button>
            <Button
              size="lg"
              disabled={isSubmitting}
              isLoading={isSubmitting}
              onClick={() => setIsDialogConfirmationOpen(false)}
            >
              Tambah poin
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default forwardRef<
  SKPTabHandler,
  PerencanaanTabPengembanganKompetensiProps
>(TabPengembanganKompetensi)
